import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { Box, IconButton, Typography } from '@mui/material';

import { TOAST_TYPES } from '@/components/UIComponents/CommonToast';

export type ToastType = (typeof TOAST_TYPES)[keyof typeof TOAST_TYPES];

const TYPE_CONFIG = {
  [TOAST_TYPES.SUCCESS]: {
    bg: '#ECFDF5',
    border: '#A7F3D0',
    iconColor: '#10B981',
    IconGlyph: TaskAltIcon,
    ariaLive: 'polite' as const,
    role: 'status' as const,
  },
  [TOAST_TYPES.ERROR]: {
    bg: '#FEF2F2',
    border: '#FECACA',
    iconColor: '#EF4444',
    IconGlyph: CancelOutlinedIcon,
    ariaLive: 'assertive' as const,
    role: 'alert' as const,
  },
  [TOAST_TYPES.WARNING]: {
    bg: '#FFFBEB',
    border: '#FDE68A',
    iconColor: '#F59E0B',
    IconGlyph: PriorityHighIcon,
    ariaLive: 'polite' as const,
    role: 'status' as const,
  },
  [TOAST_TYPES.INFO]: {
    bg: '#EFF6FF',
    border: '#BFDBFE',
    iconColor: '#3B82F6',
    IconGlyph: InfoOutlinedIcon,
    ariaLive: 'polite' as const,
    role: 'status' as const,
  },
};

interface CommonToastProps {
  type?: ToastType;
  message: string;
  onClose?: () => void;
}

const CommonToast = ({ type = TOAST_TYPES.INFO, message, onClose }: CommonToastProps) => {
  const cfg = TYPE_CONFIG[type] ?? TYPE_CONFIG[TOAST_TYPES.INFO];
  const { IconGlyph, bg, border, iconColor, ariaLive, role } = cfg;

  return (
    <Box
      role={role}
      aria-live={ariaLive}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        py: '12px',
        px: '14px',
        borderRadius: '12px',
        border: `1px solid ${border}`,
        backgroundColor: bg,
        boxShadow: '0 10px 15px -3px rgba(15,23,42,0.08), 0 4px 6px -4px rgba(15,23,42,0.06)',
        width: '100%',
        maxWidth: 400,
      }}
    >
      {/* ✅ Outlined circle icon — matches smoothui style */}
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          backgroundColor: 'transparent',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          color: iconColor,
        }}
        aria-hidden
      >
        <IconGlyph sx={{ fontSize: 20 }} />
      </Box>

      <Typography
        component="p"
        sx={{
          flex: 1,
          minWidth: 0,
          m: 0,
          fontSize: 13,
          fontWeight: 500,
          lineHeight: 1.45,
          color: '#0F172A',
          fontFamily: 'inherit',
        }}
      >
        {message}
      </Typography>

      {onClose && (
        <IconButton
          size="small"
          onClick={onClose}
          aria-label="Dismiss notification"
          sx={{
            p: 0.25,
            color: '#94A3B8',
            flexShrink: 0,
            '&:hover': { color: '#1E293B', backgroundColor: 'rgba(0,0,0,0.04)' },
          }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      )}
    </Box>
  );
};

export default CommonToast;
export { TYPE_CONFIG };
